import React, { useEffect, useMemo, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Editor } from '@tinymce/tinymce-react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Editor as TinyMCEEditor } from 'tinymce';
import {
  strapiFetchBeritaAcaraSecondaryDataTemplate,
  strapiFetchSecondaryDataBiologyTemplates,
  strapiSaveBeritaAcaraSecondaryDataHtml,
  type BiologySecondaryDataTemplates,
} from '../../../api/strapiCrm';
import { useAuth } from '../../../auth/AuthContext';
import { queryKeys } from '../../../query/queryKeys';
import { isBiologySecondaryGroup, secondaryDataSpec } from './secondaryDataHtml';
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/skin.min.css';

const EDITOR_CONTENT_STYLE = `
  body {
    margin: 16px 20px 32px;
    background: #fff;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #111;
    line-height: 1.35;
  }
  h2.section {
    text-align: center;
    font-size: 13px;
    margin: 0 0 12px;
  }
  table.data {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin: 0 0 8px;
  }
  table.data th,
  table.data td {
    border: 1px solid #222;
    padding: 4px 6px;
    vertical-align: middle;
    font-size: 12px;
    text-align: center;
  }
  table.data th {
    font-weight: 700;
  }
  table.data tr.bio-head th,
  table.data tr.bio-group td {
    background: #c5e0b3;
  }
  table.data tr.bio-group td,
  table.data td.bio-species {
    text-align: left;
  }
  table.data tr.bio-group td,
  table.data tr.bio-total td {
    font-weight: 700;
  }
  table.data td.bio-species {
    font-style: italic;
  }
  table.bio-note {
    width: 100%;
    border-collapse: collapse;
    margin-top: 12px;
  }
  table.bio-note td {
    border: 0;
    padding: 1px 4px;
    text-align: left;
    vertical-align: top;
  }
  table.bio-eval {
    margin: 8px 0 12px;
  }
  sub, sup {
    font-size: 0.75em;
    line-height: 0;
  }
`;

export type SecondaryDataHtmlDialogProps = {
  open: boolean;
  beritaAcaraDocumentId: string;
  lhuGroupedName: string;
  storedHtml: string;
  onClose: () => void;
  onSaved: (html: string) => void;
};

export function SecondaryDataHtmlDialog({
  open,
  beritaAcaraDocumentId,
  lhuGroupedName,
  storedHtml,
  onClose,
  onSaved,
}: SecondaryDataHtmlDialogProps) {
  const { jwt } = useAuth();
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const spec = useMemo(() => secondaryDataSpec(lhuGroupedName), [lhuGroupedName]);
  const isBiology = isBiologySecondaryGroup(lhuGroupedName);
  const biologyTemplatesRef = useRef<BiologySecondaryDataTemplates>({ plankton: '', bentos: '' });
  const templateQ = useQuery({
    queryKey: queryKeys.secondaryDataTemplate(jwt, beritaAcaraDocumentId || null),
    queryFn: () => strapiFetchBeritaAcaraSecondaryDataTemplate(jwt!, beritaAcaraDocumentId),
    enabled: Boolean(open && jwt && beritaAcaraDocumentId && spec),
    staleTime: 0,
  });
  const biologyQ = useQuery({
    queryKey: queryKeys.secondaryDataBiologyTemplates(jwt),
    queryFn: () => strapiFetchSecondaryDataBiologyTemplates(jwt!),
    enabled: Boolean(open && jwt && isBiology),
    staleTime: Infinity,
  });
  if (biologyQ.data) biologyTemplatesRef.current = biologyQ.data;
  const templateHtml = templateQ.data ?? '';
  const initialHtml = storedHtml.trim() ? storedHtml : templateHtml;
  const waitingForTemplate = !storedHtml.trim() && templateQ.isPending;

  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    setSaveSuccess(false);
  }, [open, beritaAcaraDocumentId]);

  const saveM = useMutation({
    mutationFn: async (html: string) => {
      if (!jwt) throw new Error('Sesi login tidak ditemukan.');
      if (!beritaAcaraDocumentId) throw new Error('Berita acara tidak ditemukan.');
      return strapiSaveBeritaAcaraSecondaryDataHtml(jwt, beritaAcaraDocumentId, html);
    },
    onSuccess: (html) => {
      setSaveError(null);
      setSaveSuccess(true);
      onSaved(html);
    },
    onError: (err: unknown) => {
      setSaveSuccess(false);
      setSaveError(err instanceof Error ? err.message : 'Gagal menyimpan secondary data.');
    },
  });

  const refreshTemplate = async () => {
    if (!spec) return;
    setSaveError(null);
    setSaveSuccess(false);
    const result = await templateQ.refetch();
    if (result.error) {
      setSaveError(result.error instanceof Error ? result.error.message : 'Gagal memuat template.');
      return;
    }
    const html = result.data ?? '';
    editorRef.current?.setContent(html);
    saveM.mutate(html);
  };

  if (!spec) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saveM.isPending) onClose();
      }}
      fullWidth
      maxWidth="lg"
      disableEnforceFocus
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      data-testid="laboratory-result-secondary-data-dialog"
      PaperProps={{ sx: { height: { xs: '100%', sm: '90vh' } } }}
    >
      <GlobalStyles styles={{ '.tox-tinymce-aux, .tox-dialog-wrap': { zIndex: '2000 !important' } }} />
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
        <Box component="span" sx={{ flex: 1 }}>
          {spec.buttonLabel}
        </Box>
        <Tooltip title="Muat ulang template dan simpan">
          <span>
            <IconButton
              aria-label="Muat ulang template"
              onClick={() => {
                void refreshTemplate();
              }}
              disabled={saveM.isPending || templateQ.isFetching}
              data-testid="laboratory-result-secondary-data-refresh"
            >
              {saveM.isPending || templateQ.isFetching ? (
                <CircularProgress size={22} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 0 }}>
        {saveError ? (
          <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        ) : null}
        {saveSuccess ? (
          <Alert severity="success" sx={{ m: 2, mb: 0 }} onClose={() => setSaveSuccess(false)}>
            Secondary data tersimpan.
          </Alert>
        ) : null}
        {templateQ.isError ? (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {templateQ.error instanceof Error ? templateQ.error.message : 'Gagal memuat template.'}
          </Alert>
        ) : null}
        {biologyQ.isError ? (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {biologyQ.error instanceof Error
              ? biologyQ.error.message
              : 'Gagal memuat template plankton dan bentos.'}
          </Alert>
        ) : null}
        <Box sx={{ flex: 1, minHeight: 420, '& .tox-tinymce': { height: '100% !important', border: 0 } }}>
          {waitingForTemplate ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : null}
          {open && !waitingForTemplate ? (
            <SecondaryDataEditor
              key={storedHtml.trim() ? beritaAcaraDocumentId : `${beritaAcaraDocumentId}:${templateHtml.length}`}
              initialHtml={initialHtml}
              disabled={saveM.isPending}
              biologyTemplatesRef={isBiology ? biologyTemplatesRef : undefined}
              onReady={(editor) => {
                editorRef.current = editor;
              }}
            />
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => editorRef.current?.setContent(templateHtml)}
          disabled={saveM.isPending || templateQ.isPending || !templateHtml}
          data-testid="laboratory-result-secondary-data-reset"
        >
          Kembalikan template
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saveM.isPending}>
          Tutup
        </Button>
        <Button
          variant="contained"
          disabled={saveM.isPending}
          data-testid="laboratory-result-secondary-data-save"
          onClick={() => {
            setSaveError(null);
            setSaveSuccess(false);
            const html = editorRef.current?.getContent() ?? initialHtml;
            saveM.mutate(html);
          }}
        >
          {saveM.isPending ? 'Menyimpan…' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function appendSecondaryTemplate(editor: TinyMCEEditor, html: string) {
  const snippet = html.trim();
  if (!snippet) return;
  editor.focus();
  const current = editor.getContent({ format: 'html' });
  const gap = current.trim() ? '<p>&nbsp;</p>' : '';
  editor.undoManager.transact(() => {
    editor.selection.select(editor.getBody(), true);
    editor.selection.collapse(false);
    editor.insertContent(`${gap}${snippet}`);
  });
  const end = editor.selection.getNode();
  if (end) editor.selection.scrollIntoView(end);
  editor.nodeChanged();
}

function bindBiologyTemplateButtons(
  editor: TinyMCEEditor,
  templatesRef: React.MutableRefObject<BiologySecondaryDataTemplates>,
) {
  editor.ui.registry.addButton('templatePlankton', {
    text: 'Template Plankton',
    tooltip: 'Tambahkan format HTML plankton di bawah isi saat ini',
    onAction: () => appendSecondaryTemplate(editor, templatesRef.current.plankton),
  });
  editor.ui.registry.addButton('templateBentos', {
    text: 'Template Bentos',
    tooltip: 'Tambahkan format HTML bentos di bawah isi saat ini',
    onAction: () => appendSecondaryTemplate(editor, templatesRef.current.bentos),
  });
}

function SecondaryDataEditor({
  initialHtml,
  disabled,
  biologyTemplatesRef,
  onReady,
}: {
  initialHtml: string;
  disabled: boolean;
  biologyTemplatesRef?: React.MutableRefObject<BiologySecondaryDataTemplates>;
  onReady: (editor: TinyMCEEditor) => void;
}) {
  const biologyToolbar = biologyTemplatesRef ? ' | templatePlankton templateBentos' : '';
  return (
    <Editor
      licenseKey="gpl"
      disabled={disabled}
      initialValue={initialHtml}
      onInit={(_evt, editor) => onReady(editor)}
      init={{
        height: '100%',
        menubar: false,
        toolbar_mode: 'wrap',
        branding: false,
        promotion: false,
        statusbar: false,
        resize: false,
        skin: false,
        content_css: false,
        content_style: EDITOR_CONTENT_STYLE,
        convert_urls: false,
        plugins: 'lists table',
        toolbar: `undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | table${biologyToolbar}`,
        setup: biologyTemplatesRef
          ? (editor) => bindBiologyTemplateButtons(editor, biologyTemplatesRef)
          : undefined,
        font_family_formats:
          'Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times,serif',
        font_size_formats: '10px 11px 12px 13px 14px 16px 18px',
        table_default_attributes: { class: 'data', border: '1' },
        table_default_styles: { 'border-collapse': 'collapse', width: '100%' },
        table_header_type: 'sectionCells',
        table_toolbar:
          'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
      }}
    />
  );
}
